<template lang="pug">
.scroll-page
  .scroll-page__content.scroller(
    :style="{ '--100cqh': customCqh }"
    ref="container"
    role="main"
  )
    .scroll-page__content-wrapper
      slot
      button.sr-only.uc-end-of-content(
        v-if="!isEditModeActive && !wordDoc && !audioDoc"
        type="button"
        @click="goToMenu"
      )
        uc-text(
          text="End of page content. Press space to go to the menu or press Tab to leave the course."
          type="span"
        )
</template>

<script lang="ts">
import gsap from 'gsap';
import $ from 'jquery';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { setCompleted, setTags } from '../lib/SCORM';
import useSuspend from '../lib/useSuspend';

import {
  computed,
  nextTick,
  ref,
  onMounted
  // onUnmounted,
  // onBeforeUnmount
} from 'vue';
import { ScrollTrigger } from 'gsap/all';
import { stop as stopAudio } from '../lib/audio';
import { useEventListener } from '@vueuse/core';
import { debounce } from 'lodash-es';

gsap.registerPlugin(ScrollToPlugin);

export default {
  name: 'UcScrollPage',
  inject: [
    'unlockScreens',
    'content',
    'moreButtons',
    'isEditModeActive',
    'wordDoc',
    'audioDoc'
  ],

  provide() {
    return {
      currentSection: computed(() => this.currentSection),
      sections: computed(() => this.sections),
      unlockedSections: computed(() => this.unlockedSections),
      page: computed(() => this.page)
      // refreshSections: computed(() => this.refreshSections)
    };
  },

  props: {
    type: { type: String, default: '' },
    computeHeight: { type: Boolean, default: false }
  },

  setup(props) {
    const suspend = useSuspend();
    const customCqh = ref('100vh');
    const container = ref<HTMLElement>();

    // set var(--100cqh) to the height of the container
    // used by lightbox pages for .min-h-uc-screen
    if (props.computeHeight) {
      const updateCqh = () => {
        if (container.value)
          customCqh.value = container.value?.offsetHeight + 'px';
      };

      onMounted(updateCqh);
      useEventListener('resize', updateCqh);
    }

    return { suspend, container, customCqh };
  },

  data(): {
    currentSection: HTMLElement | null;
    sections: HTMLElement[];
    unlockedSections: HTMLElement[];
    page: HTMLElement | null;
  } {
    return {
      currentSection: null,
      sections: [],
      unlockedSections: [],
      page: null
    };
  },
  watch: {
    isEditModeActive(curr, prev) {
      if (curr !== prev) {
        // trigger the tagschanged event to redraw the sections
        setTags();
      }
    },
    unlockedSections(next, prev) {
      if (!prev || !prev.length) {
        return;
      }

      const prevParsed = prev.split('|').filter(Boolean);
      const nextParsed = next.split('|').filter(Boolean);
      const newlyUnlocked = nextParsed.filter(id => !prevParsed.includes(id));

      this.page?.refreshSections();

      gsap.set(newlyUnlocked, { height: 'auto' });
    }
  },
  mounted() {
    const container = this.$refs.container;
    const page = container.closest('.page');
    this.page = page;

    const getSections = () =>
      [...container.getElementsByClassName('section')] as HTMLElement[];

    this.sections = getSections();
    this.currentSection = this.sections[0];

    // only refresh once per Vue tick
    let alreadyRefreshing = false;

    const refreshSections = async () => {
      this.updateUnlockedSections();

      if (alreadyRefreshing) {
        console.log('already refreshing');
        return;
      }

      alreadyRefreshing = true;

      await nextTick();

      return new Promise(resolve => {
        window.requestAnimationFrame(() => {
          ScrollTrigger.refresh();
          alreadyRefreshing = false;
          nextTick(() => resolve(1));
        });
      });
    };

    page.refreshSections = refreshSections;

    const debouncedRefreshSections = debounce(() => {
      void refreshSections();
    }, 300);

    /**
     * Programmatic scroll (scrollToSection): bump generation on start; finish sets settled gen + currentSection.
     * While generation !== settled, ignore scrollsectionentered (avoids wrong active section mid-smooth-scroll).
     */
    let programmaticScrollGeneration = 0;
    let programmaticScrollSettledGeneration = 0;

    // Check if smooth scrolling is supported
    const supportsSmoothScroll =
      'scrollBehavior' in document.documentElement.style;

    const scrollToSection = (el, opts: GSAPTweenVars = {}) => {
      // force any selector to DOM node
      el = $(el)[0];

      const myGen = ++programmaticScrollGeneration;

      // Calculate scroll duration before initiating scroll
      let scrollDuration = 0;
      if (!this.suspend.reducedMotion) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = el.getBoundingClientRect();
        const distance = Math.abs(targetRect.top - containerRect.top);
        scrollDuration = Math.min(Math.max(distance / 1000, 0.3), 0.8); // Between 300ms and 800ms
      }

      const finishProgrammaticScroll = () => {
        if (myGen !== programmaticScrollGeneration) return;
        programmaticScrollSettledGeneration = myGen;
        this.currentSection = el;
        void refreshSections();
      };

      if (supportsSmoothScroll && !this.suspend.reducedMotion) {
        // Use native scrollIntoView with smooth behavior
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      } else {
        // Fallback for browsers that don't support smooth scrolling
        // or when reduced motion is enabled
        el.scrollIntoView({
          block: 'start',
          inline: 'nearest'
        });
      }

      // Apply active section + refresh after scroll settles (avoids mid-animation desync)
      if (!this.suspend.reducedMotion) {
        setTimeout(() => {
          requestAnimationFrame(finishProgrammaticScroll);
        }, scrollDuration * 1000);
      } else {
        requestAnimationFrame(finishProgrammaticScroll);
      }
    };

    const findNeighbor = (el, dir = 'next') => {
      // find target section
      const i = this.sections.findIndex(s => s === el);

      // find this section's neighbour excluding hidden sections
      let neighbor;
      if (dir === 'next') {
        neighbor = this.sections
          .slice(i + 1)
          .find(s => s.getAttribute('data-section-hidden') !== 'true');
      } else {
        neighbor = this.sections
          .slice(0, i)
          .reverse()
          .find(s => s.getAttribute('data-section-hidden') !== 'true');
      }

      return neighbor;
    };

    const isTall = el =>
      el?.getBoundingClientRect().height > window.innerHeight + 5;

    const scrollOne = (
      dir = 'next',
      {
        from,
        force = false,
        ...opts
      }: {
        from?: string | HTMLElement;
        force?: boolean;
        opts?: GSAPTweenVars;
      } = {}
    ) => {
      const el = from ? $(from)[0] : this.currentSection;
      const target = findNeighbor(el, dir);

      if (!force) {
        if (isTall(el) || (isTall(target) && dir !== 'next')) {
          return;
        }
      }

      stopAudio({ fadeOut: 1 });

      // first or last slide may not have a target
      if (target) {
        scrollToSection(target, opts);
        return true;
      }
    };

    container.scrollToSection = scrollToSection;
    container.scrollOne = scrollOne;
    page.scrollToSection = scrollToSection;
    page.scrollOne = scrollOne;

    const onKeydown = async e => {
      let scrolled;

      if (['PageDown'].includes(e.key)) {
        const target = findNeighbor(this.currentSection, 'next');
        if (
          !target ||
          (this.unlockedSections as unknown as string).includes(
            '|' + target.id + '|'
          )
        ) {
          scrolled = scrollOne('next');
        }
      }

      if (['PageUp'].includes(e.key)) {
        scrolled = scrollOne('prev');
      }

      if (e.code === 'KeyN' && e.altKey) {
        const activeSlides = document.querySelector('.section--active .slides');

        const isLastSlideActive = document.querySelector(
          '.section--active .slide--active:last-child'
        );

        if (activeSlides && !isLastSlideActive) {
          setCompleted(
            document.querySelector('.section--active .slide--active').id
          );
          await refreshSections();
          if (activeSlides.next) activeSlides.next();
        } else {
          setCompleted(this.currentSection.id);
          refreshSections().then(() => scrollOne('next', { force: true }));
          scrolled = true;
        }
      }

      if (scrolled) {
        e.preventDefault();
      }
    };

    page.addEventListener('keydown', onKeydown);

    nextTick().then(() => {
      if (!this.moreButtons) setCompleted(this.sections[0].id);
      refreshSections();
    });

    page.addEventListener('scrollsectionentered', (e: Event) => {
      const target = e.target as HTMLElement;
      if (
        !(target instanceof HTMLElement) ||
        !target.classList.contains('section')
      ) {
        return;
      }
      if (
        programmaticScrollGeneration !== programmaticScrollSettledGeneration
      ) {
        return;
      }
      this.currentSection = target;
      debouncedRefreshSections();
    });

    window.addEventListener('pageenter--' + page.id, async () => {
      container.focus();
      await refreshSections();
    });

    window.addEventListener('tagschanged', async () => {
      this.sections = getSections();
      await refreshSections();
    });

    this.$el.dispatchEvent(new CustomEvent('uc:scrollpagemounted'));
  },
  methods: {
    goToMenu() {
      const menuButton = document.querySelector(
        '.uc-nav-menu .menu__button'
      ) as HTMLElement | null;
      menuButton?.focus({ preventScroll: true });
      menuButton?.click();
    },
    updateUnlockedSections() {
      // hunt backwards until finding a completed section
      const sections = this.sections.filter(
        el => !el.matches('[data-section-hidden="true"]')
      );

      let i = sections.length - 1;
      let foundLast;
      let lastIndex = 0;

      if (i < 1) {
        return this.moreButtons ? [] : [sections[i].id];
      }

      while (i >= 0 && !foundLast) {
        const section = sections[i] as HTMLElement;
        const id = section.id;

        if (this.suspend.completed[id]) {
          foundLast = true;
          lastIndex = i + 1;
        }

        i--;
      }

      const lastSection = sections[lastIndex];
      const lastSectionFirstNested = lastSection?.querySelector('.section');
      if (lastSectionFirstNested) lastIndex++;

      const lastEl = document.getElementById(sections[lastIndex]?.id);

      if (lastEl?.querySelector('section.section')) lastIndex++;

      // complete page when last or second-to-last section is complete
      if (lastIndex >= sections.length - 1) {
        setCompleted(this.page.id);
      }

      this.unlockedSections =
        '|' +
        sections
          .map(el => el.id)
          .filter(
            (_, ii) =>
              (this.unlockScreens && this.type !== 'final') || ii <= lastIndex
          )
          .join('|') +
        '|';
    }
  }
};
</script>

<style scoped>
.scroll-page {
  position: absolute;
  inset: 0;
  margin: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  outline: none !important;
}

.scroll-page__content {
  position: absolute;
  inset: 0;
  margin: 0;
  height: 100%;
  width: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  outline: none;
  z-index: 0;
}
</style>
